/**
 * rateLimiter.ts
 * Client-side rate limiting for Homphy write operations.
 * This is a first-line defence — always pair with Firestore security rules.
 */

interface RateLimitConfig {
  maxCalls: number;   // max calls allowed
  windowMs: number;   // rolling window in milliseconds
}

// In-memory store: operationKey → timestamps of recent calls
const callLog = new Map<string, number[]>();

/**
 * Check and record a rate-limited operation.
 * Throws an error if the limit is exceeded.
 *
 * @param key       Unique key: `operationName:userId`
 * @param config    Limit config
 */
export function checkRateLimit(key: string, config: RateLimitConfig): void {
  const now = Date.now();
  const windowStart = now - config.windowMs;

  // Get existing timestamps, filter to current window
  const existing = (callLog.get(key) || []).filter(ts => ts > windowStart);

  if (existing.length >= config.maxCalls) {
    const oldestInWindow = Math.min(...existing);
    const waitMs = (oldestInWindow + config.windowMs) - now;
    const waitSec = Math.ceil(waitMs / 1000);
    throw new Error(`Too many requests. Please wait ${waitSec} second${waitSec !== 1 ? 's' : ''} before trying again.`);
  }

  // Record this call
  existing.push(now);
  callLog.set(key, existing);
}

// ── Pre-configured limiters for each operation ──────────────────────────────

/** Max 3 bookings per user per 10 minutes */
export const BOOKING_LIMIT: RateLimitConfig = {
  maxCalls: 3,
  windowMs: 10 * 60 * 1000,
};

/** Max 2 reviews per user per 24 hours */
export const REVIEW_LIMIT: RateLimitConfig = {
  maxCalls: 2,
  windowMs: 24 * 60 * 60 * 1000,
};

/** Max 10 messages per user per 30 seconds */
export const MESSAGE_LIMIT: RateLimitConfig = {
  maxCalls: 10,
  windowMs: 30 * 1000,
};

/** Max 3 help tickets per user per hour */
export const TICKET_LIMIT: RateLimitConfig = {
  maxCalls: 3,
  windowMs: 60 * 60 * 1000,
};

/** Max 5 physio need submissions per user per hour */
export const PHYSIO_NEED_LIMIT: RateLimitConfig = {
  maxCalls: 5,
  windowMs: 60 * 60 * 1000,
};

/** Max 2 payment submissions per user per 30 minutes */
export const PAYMENT_LIMIT: RateLimitConfig = {
  maxCalls: 2,
  windowMs: 30 * 60 * 1000,
};

/** Max 3 registration attempts per session (anonymous key) */
export const REGISTER_LIMIT: RateLimitConfig = {
  maxCalls: 3,
  windowMs: 15 * 60 * 1000,
};

// ── Input validation helpers ─────────────────────────────────────────────────

/** Validates a Kenyan phone number: 07XXXXXXXX, +2547XXXXXXXX, 2547XXXXXXXX */
export function isValidKenyanPhone(phone: string): boolean {
  return /^(07|\+2547|2547)\d{8}$/.test(phone.replace(/\s/g, ''));
}

/** Validates a Kenyan National ID: 7-8 digits */
export function isValidNationalId(id: string): boolean {
  return /^\d{7,8}$/.test(id.trim());
}

/** Validates a name: 2-80 chars, letters/spaces/hyphens/apostrophes/dots only */
export function isValidName(name: string): boolean {
  return (
    name.trim().length >= 2 &&
    name.length <= 80 &&
    /^[a-zA-Z\s\-'\.]+$/.test(name.trim())
  );
}

/** Validates a practice year: between 1970 and current year */
export function isValidPracticeYear(year: number): boolean {
  const current = new Date().getFullYear();
  return year >= 1970 && year <= current;
}

/** Validates a PCK registration number: alphanumeric, 4-20 chars */
export function isValidRegistrationNumber(reg: string): boolean {
  return /^[A-Z0-9\-\/]{4,20}$/.test(reg.trim().toUpperCase());
}

/** File type and size validator */
export function validateImageFile(
  file: File,
  options?: { maxSizeMB?: number; allowedTypes?: string[] }
): void {
  const maxSizeMB = options?.maxSizeMB ?? 5;
  const allowedTypes = options?.allowedTypes ?? ['image/jpeg', 'image/png', 'image/webp'];

  if (!allowedTypes.includes(file.type)) {
    throw new Error(`Invalid file type. Allowed: ${allowedTypes.map(t => t.split('/')[1].toUpperCase()).join(', ')}`);
  }

  if (file.size > maxSizeMB * 1024 * 1024) {
    throw new Error(`File too large. Maximum size is ${maxSizeMB}MB`);
  }
}

/** Validates and normalizes a Kenyan phone number to 07XXXXXXXX format */
export function normalizeKenyanPhone(phone: string): string {
  const cleaned = phone.replace(/\s/g, '');
  if (cleaned.startsWith('+254')) return '0' + cleaned.slice(4);
  if (cleaned.startsWith('254')) return '0' + cleaned.slice(3);
  return cleaned;
}

/** Generic message length validator */
export function validateMessageLength(text: string, max = 2000): void {
  if (text.length > max) {
    throw new Error(`Message too long. Maximum ${max} characters allowed.`);
  }
}